import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { Button, Col, Collapse, Row } from 'react-bootstrap';
import CustomerDetail from './CustomerDetail';
import OrderModalReadyGuard from './OrderModalReadyGuard';
import SaleAssistantDetail from './SaleAssistantDetail';
import OrderBusinessDetail from './OrderBusinessDetail';
import OrderNotesDetail from './OrderNotesDetail';
import OrderApprovalReasonDetail from './OrderApprovalReasonDetail';
import OrderAttachmentList from './OrderAttachmentList';
import ProductListDetail from './ProductListDetail';
import PaymentDetail from './PaymentDetail';
import OrderContractsDetail from './OrderContractsDetail';
import OrderInvoicesDetail from './OrderInvoicesDetail';
import { useOrderContext } from '../../../../../providers/OrderProvider';
import OrderPerformanceRulesSection from './OrderPerformanceRulesSection';
import OrderFinalCostSection from './OrderFinalCostSection';
import OrderRewardMediaContent from './OrderRewardMediaContent';
import OrderDeliveryDetail from './OrderDeliveryDetail';
import OrderPaymentDetail from './OrderPaymentDetail';
import OrderPaymentScheduleDetail from './OrderPaymentScheduleDetail';
import { OrderCorrectionDraftProvider } from './OrderCorrectionDraftContext';
import OrderApprovalHistory from '../../../../order/OrderApprovalHistory';

/**
 * 订单详情 Modal 统一内容区（列表详情、仪表盘待办等共用）
 */
const OrderDetailModalBody = ({
	notesType = 'pay',
	pendingFocus,
	paymentScheduleModelName,
	showOrderProgress = false,
	contractTooltipId = 'order-detail-contract-toggle-tooltip',
	invoiceTooltipId = 'order-detail-invoice-toggle-tooltip',
	approvalReason,
	approvalDetail,
}) => {
	const { order } = useOrderContext();
	const isLegacyCompleted = Boolean(order?.uiHints?.isLegacyCompletedBeforeFinalCostCutoff);
	const isApprovalRejected = order?.approvalStatus === 'reject';
	const isPrePaymentFocus = pendingFocus === 'prePayment';
	const isContractFocus = pendingFocus === 'contract';
	const isDeliveryFocus = pendingFocus === 'delivery';
	const isAcceptFocus = pendingFocus === 'accept';
	const isApprovalFocus = pendingFocus === 'approval';
	const [showMoreOrderInfo, setShowMoreOrderInfo] = useState(() => Boolean(pendingFocus));

	useEffect(() => {
		if (pendingFocus) {
			setShowMoreOrderInfo(true);
		}
	}, [pendingFocus]);

	const orderInfoGrid = ({ includePayment, includeContract = true, includeDelivery = true, includeProgress = true, orderInfoLayout = 'default' }) => {
		const showContract = includeContract && !isApprovalRejected;
		const showDelivery = includeDelivery && !isApprovalRejected;
		const showPayment = includePayment && !isApprovalRejected;
		const showInvoice = !isApprovalRejected;
		const showProgressBlocks = includeProgress && !isApprovalRejected && !isLegacyCompleted;

		// 主区已展示物流时，更多信息内不再留空的「物流侧栏」，改单列堆叠
		const useSplitOrderLayout = showDelivery;

		const orderInfoBlocks =
			orderInfoLayout === 'splitSale' ? (
				<Row className='g-3 align-items-start'>
					<Col sm={6}>
						<CustomerDetail />
						<OrderBusinessDetail />
						<OrderNotesDetail type={notesType} />
					</Col>
					<Col sm={6}>
						<SaleAssistantDetail />
					</Col>
				</Row>
			) : (
				<>
					<CustomerDetail />
					<SaleAssistantDetail />
					<OrderBusinessDetail />
					<OrderNotesDetail type={notesType} />
				</>
			);

		const ancillaryBlocks = (
			<>
				{showContract ? <OrderContractsDetail contractTooltipId={contractTooltipId} /> : null}
				{showDelivery ? <OrderDeliveryDetail /> : null}
			</>
		);

		return (
			<>
				<Row className='g-3 align-items-start'>
					<Col xs={12} lg={8}>
						{useSplitOrderLayout ? (
							<Row className='g-3 align-items-start'>
								<Col xs={12} md={6}>
									{orderInfoBlocks}
								</Col>
								<Col xs={12} md={6}>
									{ancillaryBlocks}
								</Col>
							</Row>
						) : (
							<>
								{orderInfoBlocks}
								{ancillaryBlocks}
							</>
						)}
					</Col>
					<Col xs={12} lg={4}>
						{showInvoice ? <OrderInvoicesDetail invoiceTooltipId={invoiceTooltipId} /> : null}
						{showPayment ? <OrderPaymentDetail modelName={paymentScheduleModelName} /> : null}
						{showProgressBlocks ? (
							<>
								<OrderRewardMediaContent forceVisible />
								<OrderPerformanceRulesSection embedded forceVisible />
								<OrderFinalCostSection forceVisible />
							</>
						) : null}
					</Col>
				</Row>
				<Row className='mt-2'>
					<Col xs={12}>
						<ProductListDetail />
					</Col>
				</Row>
				{!isApprovalFocus && Array.isArray(order?.approvalHistory) && order.approvalHistory.length > 0 ? (
					<Row className='mt-3'>
						<Col xs={12}>
							<OrderApprovalHistory history={order.approvalHistory} className='mb-0' />
						</Col>
					</Row>
				) : null}
				<OrderAttachmentList />
			</>
		);
	};

	const moreInfoToggle = (
		<Button
			variant='link'
			size='sm'
			className='d-inline-flex align-items-center gap-1 px-0 mb-2 fs-10 text-decoration-none'
			onClick={() => setShowMoreOrderInfo((v) => !v)}
			aria-expanded={showMoreOrderInfo}
		>
			<FontAwesomeIcon icon={showMoreOrderInfo ? 'chevron-up' : 'chevron-down'} className='fa-fw' />
			{showMoreOrderInfo ? '收起更多信息' : '查看更多信息'}
		</Button>
	);

	return (
		<OrderCorrectionDraftProvider>
			<OrderModalReadyGuard>
			{isPrePaymentFocus ? (
				<>
					<section className='order-detail-modal-body__focus-primary border rounded-3 bg-body-tertiary px-3 py-3 mb-2'>
						<div className='d-flex align-items-center gap-2 mb-1'>
							<FontAwesomeIcon icon='sack-dollar' className='text-warning' />
							<h5 className='mb-0 fs-9'>预付款确认</h5>
						</div>
						<p className='fs-10 text-600 mb-3'>请核对预付款金额，并在下方节点完成收款确认。</p>
						<Row className='g-3 align-items-start'>
							<Col lg={3} md={4}>
								<PaymentDetail />
							</Col>
							<Col lg={9} md={8}>
								<OrderPaymentScheduleDetail pendingStepFocus='prePayment' isLast modelName={paymentScheduleModelName} />
							</Col>
						</Row>
					</section>
					{moreInfoToggle}
					<Collapse in={showMoreOrderInfo}>
						<div className='order-detail-modal-body__more pb-3'>{orderInfoGrid({ includePayment: false })}</div>
					</Collapse>
				</>
			) : isContractFocus || isDeliveryFocus ? (
				<>
					<section className='order-detail-modal-body__focus-primary border rounded-3 bg-body-tertiary px-3 py-3 mb-2'>
						<div className='d-flex align-items-center gap-2 mb-1'>
							<FontAwesomeIcon icon='truck' className='text-warning' />
							<h5 className='mb-0 fs-9'>{isContractFocus ? '待签约' : '待发货'}</h5>
						</div>
						<p className='fs-10 text-600 mb-3'>请核对物流进度并确认发货/提供服务，可同时补充合同编号。</p>
						<Row className='g-3 align-items-start'>
							<Col xs={12} lg={7}>
								<OrderDeliveryDetail pendingStepFocus='delivery' />
							</Col>
							<Col xs={12} lg={5}>
								<OrderContractsDetail contractTooltipId={contractTooltipId} />
							</Col>
						</Row>
					</section>
					{moreInfoToggle}
					<Collapse in={showMoreOrderInfo}>
						<div className='order-detail-modal-body__more pb-3'>
							{orderInfoGrid({
								includePayment: true,
								includeContract: false,
								includeDelivery: false,
								orderInfoLayout: isContractFocus || isDeliveryFocus ? 'splitSale' : 'default',
							})}
						</div>
					</Collapse>
				</>
			) : isAcceptFocus ? (
				<>
					<section className='order-detail-modal-body__focus-primary border rounded-3 bg-body-tertiary px-3 py-3 mb-2'>
						<div className='d-flex align-items-center gap-2 mb-1'>
							<FontAwesomeIcon icon='truck' className='text-warning' />
							<h5 className='mb-0 fs-9'>待验收</h5>
						</div>
						<p className='fs-10 text-600 mb-2'>请核对物流与到货情况，并在下方完成验收确认。</p>
						<OrderDeliveryDetail pendingStepFocus='accept' />
					</section>
					{moreInfoToggle}
					<Collapse in={showMoreOrderInfo}>
						<div className='order-detail-modal-body__more pb-3'>
							{orderInfoGrid({
								includePayment: true,
								includeContract: false,
								includeDelivery: false,
								orderInfoLayout: 'splitSale',
							})}
						</div>
					</Collapse>
				</>
			) : isApprovalFocus ? (
				<>
					<section className='order-detail-modal-body__focus-primary border rounded-3 bg-body-tertiary px-3 py-3 mb-2'>
						<Row className='g-3 align-items-start'>
							<Col xs={12} lg={6} className='min-w-0'>
								<div className='d-flex align-items-center gap-2 mb-1'>
									<FontAwesomeIcon icon='clipboard-check' className='text-warning' />
									<h5 className='mb-0 fs-9'>待审批</h5>
								</div>
								<p className='fs-10 text-600 mb-3'>请核对审批事项，确认后点击同意或不同意。</p>
								<p className='mb-1 fs-10 fw-normal'>审批事项：</p>
								<OrderApprovalReasonDetail
									embedded
									reason={approvalReason}
									detail={approvalDetail}
									onBeforeLocate={() => setShowMoreOrderInfo(true)}
								/>
							</Col>
							<Col xs={12} lg={6} className='min-w-0'>
								{Array.isArray(order?.approvalHistory) && order.approvalHistory.length > 0 ? (
									<OrderApprovalHistory history={order.approvalHistory} titleRow className='mb-0 h-100' />
								) : (
									<p className='fs-10 text-500 mb-0'>暂无审批历史</p>
								)}
							</Col>
						</Row>
					</section>
					{moreInfoToggle}
					<Collapse in={showMoreOrderInfo}>
						<div className='order-detail-modal-body__more pb-3'>{orderInfoGrid({ includePayment: true, includeContract: true })}</div>
					</Collapse>
				</>
			) : (
				orderInfoGrid({ includePayment: true, includeContract: true })
			)}
			</OrderModalReadyGuard>
		</OrderCorrectionDraftProvider>
	);
};

OrderDetailModalBody.propTypes = {
	notesType: PropTypes.string,
	pendingFocus: PropTypes.oneOf(['prePayment', 'contract', 'delivery', 'accept', 'approval']),
	paymentScheduleModelName: PropTypes.string,
	showOrderProgress: PropTypes.bool,
	contractTooltipId: PropTypes.string,
	invoiceTooltipId: PropTypes.string,
	approvalReason: PropTypes.string,
	approvalDetail: PropTypes.object,
};

export default OrderDetailModalBody;
